/* eslint-disable @typescript-eslint/no-explicit-any */
import * as tus from 'tus-js-client';
import { OAuthClientService } from '~packages/oauth-service/client';
import type {
  IUserCredentials,
  OAuthConfig,
} from '~packages/oauth-service/types';

import { download, processPromises } from '@/shared/helpers';

import type {
  IError,
  IFileInfo,
  IHeaders,
  IInitBackPayload,
  IInitBackResult,
  IUploadFileResult,
} from '../../types';

export * from '../../types/index.d';

export default class MiniOrchService {
  amAdapter;
  amBack;
  endpoint;
  oAuthService;
  tus;

  /*  */
  constructor(
    config: OAuthConfig,
    userCredentials: IUserCredentials,
    endpoint: string,
    apiMiddlewares: Record<string, any>,
    localStorageKey = 'MINIORCH_TOKEN'
  ) {
    /* Api middlewares */
    this.amAdapter = apiMiddlewares.minioAdapter({ isManual: true });
    this.amBack = apiMiddlewares.minioBack({ isManual: true });

    /* Vars */
    this.oAuthService = new OAuthClientService(
      config,
      userCredentials,
      localStorageKey
    );

    /*  */
    this.endpoint = endpoint;
    this.tus = tus;
  }

  /*  */
  _formatResult<T>(status: boolean, response?: T, errors: IError[] = []) {
    return { status, response, errors };
  }

  /*  */
  _headersGet() {
    const headers: IHeaders = {};

    const bearer = this.oAuthService.tokenMeta.access_token;

    if (bearer) {
      headers.Authorization = `Bearer ${bearer}`;
    } else {
      console.warn('NO BEARER');
    }

    return headers;
  }

  /*  */
  async _headersGetForTus(url: string): Promise<IHeaders> {
    try {
      const parsedUrl = new URL(url);
      const pathSegments = parsedUrl.pathname.split('/').filter(Boolean);

      const minioUrl = '/' + pathSegments.slice(1).join('/');
      const clusterName = pathSegments[0];

      const response = await this.awsEncodeMessage({
        method: 'GET',
        minio_url: minioUrl,
        cluster_name: clusterName,
      });

      return response.status ? response.response?.headers : {};
    } catch (err) {
      console.warn('ERROR!', err instanceof Error ? err.message : err);
      return {};
    }
  }

  /*  */
  async authenticate() {
    return await this.oAuthService.authenticate();
  }

  /*  */
  async awsEncodeMessage(data: any) {
    return await this.amAdapter.awsEncodeMessage('', {}, data, {
      options: {
        headers: this._headersGet(),
      },
    });
  }

  async blobFetchFromUrl(url: string) {
    const r = await fetch(url, {
      headers: {
        ...this._headersGet(),
        ...(await this._headersGetForTus(url)),
      },
    });

    if (!r.ok) {
      return {
        status: false,
      };
    }

    return {
      data: await r.blob(),
      status: true,
    };
  }

  /*  */
  async download(fileInfo: IFileInfo) {
    return await fetch(fileInfo.req_download_url, {
      headers: {
        ...this._headersGet(),

        /* Option 1 */
        ...fileInfo.headers.Download_headers,

        /* Option 2 */
        // ...(await this._headersGetForTus(fileInfo.req_download_url)),
      },
    });
  }

  async downloadFromUrl(
    url: string,
    name?: string,
    type?: string,
    ntAddXlsx?: boolean
  ) {
    const rBlobRes = await this.blobFetchFromUrl(url);
    if (!rBlobRes.status) return rBlobRes;

    await download((await rBlobRes).data, name, type, ntAddXlsx);
    return {
      status: true,
    };
  }

  /*  */
  async initBack(data: IInitBackPayload): IInitBackResult {
    const headers = this._headersGet();
    if (!headers?.Authorization) {
      return this._formatResult(false, null);
    }

    const response = await this.amBack.initBack(undefined, undefined, data, {
      options: {
        headers,
      },
    });

    if (response.status) {
      return this._formatResult(true, {
        _: response,
        data: response.response.data,
      });
    } else {
      console.error('Ошибка запроса!', response);

      /* TODO: Add info */
      return this._formatResult(false);
    }
  }

  /*  */
  async upload(payload: IInitBackPayload, file: File) {
    const obj = await this.initBack(payload);
    if (!obj.status || !obj.response) {
      return this._formatResult(false);
    }
    const { data } = obj.response;
    const fileInfo = data.files[0] as IFileInfo;
    return {
      status: true,
      response: {
        fileInfo,
        meta: await this.uploadFile(file, fileInfo.req_upload_url),
      },
    };
  }

  /*  */
  async uploadAndDownload(payload: IInitBackPayload, file: File) {
    const obj = await this.initBack(payload);
    if (!obj.status || !obj.response) {
      return this._formatResult(false);
    }
    const { data } = obj.response;
    const filesInfo = data.files;

    // return this.uploadAndDownloadSingle(file, filesInfo[0]);
    return this.uploadAndDownloadMultiple(file, filesInfo);
  }

  /*  */
  async uploadAndDownloadMultiple(file: File, filesInfo: IFileInfo[]) {
    const fDefault = '_default';

    const pUpload = filesInfo.map(i => this.uploadFile(file, i.req_upload_url));
    const rUpload = await processPromises(pUpload);

    const pDownload = filesInfo.map(i => this.download(i));
    const rDownload = await processPromises(pDownload);
    const results = rDownload[fDefault as keyof typeof rDownload] as Response[];

    /*  */
    const pBlob: Promise<Blob>[] = results
      .filter(r => r.status)
      .map(r => r.blob());
    const rBlob = await processPromises(pBlob);

    const blobs = rBlob[fDefault as keyof typeof rBlob] as Blob[];
    const pFiles = blobs.map(b => download(b));
    await processPromises(pFiles);

    return this._formatResult(true, { upload: rUpload });
  }

  /*  */
  async uploadAndDownloadSingle(file: File, fileInfo: IFileInfo) {
    await this.uploadFile(file, fileInfo.req_upload_url);
    const response = await this.download(fileInfo);

    if (response.status) {
      await download(await response.blob());
      return this._formatResult(true);
    } else {
      return this._formatResult(false);
    }
  }

  /*  */
  uploadFile(file: File, uploadUrl?: string): Promise<IUploadFileResult> {
    const tus = this.tus;
    if (!tus) {
      return new Promise(resolve => {
        resolve({
          status: false,
        });
      });
    }

    const bearer = this.oAuthService.tokenMeta.access_token;

    return new Promise(async resolve => {
      const options: tus.UploadOptions = {
        endpoint: this.endpoint,
        uploadUrl,
        headers: bearer
          ? {
              Authorization: `Bearer ${bearer}`,
            }
          : {},
        retryDelays: undefined,
        // metadata: {
        //   filename: file.name,
        //   filetype: file.type,
        // },
        onError: function (error: Error) {
          console.log('Failed because: ' + error);
        },
        onProgress: function (bytesUploaded: number, bytesTotal: number) {
          const percentage = ((bytesUploaded / bytesTotal) * 100).toFixed(2);
          console.log(bytesUploaded, bytesTotal, percentage + '%');
        },
        onSuccess: function () {
          console.log('done');
          const file = upload.file;
          console.log(
            'Download %s from %s',
            file instanceof File ? file.name : file,
            upload.url
          );
          resolve({
            status: true,
            data: {
              name: '',
            },
          });
        },
      };

      /*  */
      if (uploadUrl) {
        delete options.endpoint;
        options.uploadUrl = uploadUrl;
      }

      /*  */
      const upload = new tus.Upload(file, options);

      // Check if there are any previous uploads to continue.
      const previousUploads = await upload.findPreviousUploads();

      // Found previous uploads so we select the first one.
      if (previousUploads.length) {
        upload.resumeFromPreviousUpload(previousUploads[0]);
      }

      // Start the upload
      upload.start();
    });
  }
}
